// ============================================================================
// SirAccessModal.jsx — the wall the public SIR search puts up when the free
// allowance runs out.
//
// Two steps, in the order the product charges for them:
//   1. "otp"     mobile number → WhatsApp OTP → 5 more searches
//   2. "credits" allowance spent as well → buy credits
//
// It opens on whichever step the SERVER says applies (quota.needsOtp /
// quota.needsCredit). Nothing here decides entitlement — this is presentation
// over a decision already made in api_files/sirQuota.js.
// ============================================================================

import React, { useEffect, useState } from "react";
import { SIR_THEME } from "./sirConfig";
import {
  clearSirSession,
  fetchCreditPlans,
  quotaBlockMessage,
  sendSirOtp,
  verifySirOtp,
} from "./sirQuotaClient";

const OTP_LENGTH = 4;      // matches the 4-digit OTP the backend generates
const RESEND_SECONDS = 45;

const box = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  zIndex: 2000,
};

const card = {
  background: "#fff",
  borderRadius: 14,
  width: "100%",
  maxWidth: 420,
  boxShadow: "0 18px 50px rgba(0,0,0,0.3)",
  overflow: "hidden",
};

const field = {
  width: "100%",
  padding: "11px 12px",
  fontSize: 16,
  border: "1px solid #cfd8e3",
  borderRadius: 8,
  outline: "none",
};

const primary = (disabled) => ({
  width: "100%",
  padding: "11px 14px",
  fontSize: 15,
  fontWeight: 700,
  color: "#fff",
  background: disabled ? "#9db6d4" : SIR_THEME.bar,
  border: "none",
  borderRadius: 8,
  cursor: disabled ? "not-allowed" : "pointer",
});

function Note({ kind, text }) {
  if (!text) return null;
  const tone =
    kind === "error"
      ? { bg: "#fdecea", fg: "#a4271c" }
      : kind === "ok"
        ? { bg: "#e8f5e9", fg: "#1b5e20" }
        : { bg: "#eef2f7", fg: "#33475b" };
  return (
    <div
      style={{
        background: tone.bg,
        color: tone.fg,
        padding: "9px 11px",
        borderRadius: 8,
        fontSize: 13,
        marginBottom: 12,
        lineHeight: 1.45,
      }}
    >
      {text}
    </div>
  );
}

/**
 * @param {object}   props.quota      latest quota object from the server
 * @param {object}   props.block      the refusal that opened this, if any
 * @param {function} props.onClose
 * @param {function} props.onVerified called with the fresh quota after OTP
 */
function SirAccessModal({ quota, block, onClose, onVerified }) {
  // The server decides which wall this is; needsCredit means OTP is already
  // done and spent, so showing the OTP form again would be a dead end.
  const initialStep = quota?.needsCredit || block?.reason === "needCredit" ? "credits" : "otp";

  const [step, setStep] = useState(initialStep);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const [plans, setPlans] = useState([]);
  const [support, setSupport] = useState("");

  /* Resend cooldown. Purely cosmetic — the backend throttles resends too. */
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (step !== "credits") return;
    fetchCreditPlans()
      .then((res) => {
        setPlans(Array.isArray(res?.plans) ? res.plans : []);
        setSupport(res?.supportNumber || "");
      })
      .catch(() => setPlans([]));
  }, [step]);

  const handleSend = async () => {
    const digits = mobile.replace(/\D/g, "");
    if (digits.length !== 10) {
      setNote({ kind: "error", text: "Enter your 10-digit mobile number." });
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const res = await sendSirOtp(digits);
      if (res?.status === "ok") {
        setSent(true);
        setCooldown(RESEND_SECONDS);
        setNote({
          kind: "ok",
          text: res.reused
            ? "Your last OTP is still valid — enter the code we sent you on WhatsApp."
            : "OTP sent on WhatsApp to " + digits + ".",
        });
      } else {
        setNote({ kind: "error", text: res?.message || "Could not send the OTP." });
      }
    } catch (err) {
      setNote({ kind: "error", text: "Could not reach the server. Check your connection." });
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    const digits = mobile.replace(/\D/g, "");
    const code = otp.replace(/\D/g, "");
    if (code.length < OTP_LENGTH) {
      setNote({ kind: "error", text: "Enter the " + OTP_LENGTH + "-digit OTP." });
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const res = await verifySirOtp(digits, code, name.trim());
      if (res?.status === "ok") {
        // Straight to the credits wall when the verified allowance is already
        // spent — this number has searched here before.
        if (res.quota?.needsCredit) {
          setStep("credits");
          setNote({
            kind: "info",
            text: "This number has already used its 5 verified searches. Buy credits to continue.",
          });
          onVerified?.(res.quota, { keepOpen: true });
          return;
        }
        onVerified?.(res.quota);
      } else {
        setNote({ kind: "error", text: res?.message || "Verification failed." });
      }
    } catch (err) {
      setNote({ kind: "error", text: "Could not reach the server. Check your connection." });
    } finally {
      setBusy(false);
    }
  };

  const headline =
    step === "credits"
      ? "Buy search credits"
      : sent
        ? "Enter the OTP"
        : "Verify your mobile number";

  return (
    <div style={box} onClick={onClose} role="dialog" aria-modal="true">
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            background: SIR_THEME.bar,
            color: SIR_THEME.barText,
            padding: "13px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <strong style={{ fontSize: 15.5 }}>{headline}</strong>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent",
              border: "none",
              color: SIR_THEME.barText,
              fontSize: 22,
              lineHeight: 1,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 16 }}>
          {block ? <Note kind="info" text={quotaBlockMessage(block)} /> : null}
          <Note kind={note?.kind} text={note?.text} />

          {step === "otp" ? (
            <>
              <label style={{ fontSize: 12.5, color: "#5b6b7d", fontWeight: 600 }}>
                Mobile number
              </label>
              <input
                style={{ ...field, marginTop: 5, marginBottom: 12 }}
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit mobile number"
                value={mobile}
                disabled={sent}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => e.key === "Enter" && !sent && handleSend()}
              />

              {sent ? (
                <>
                  <label style={{ fontSize: 12.5, color: "#5b6b7d", fontWeight: 600 }}>
                    OTP received on WhatsApp
                  </label>
                  <input
                    style={{ ...field, marginTop: 5, marginBottom: 12, letterSpacing: 6, fontWeight: 700 }}
                    type="tel"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="••••"
                    value={otp}
                    autoFocus
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    onKeyDown={(e) => e.key === "Enter" && handleVerify()}
                  />
                  <label style={{ fontSize: 12.5, color: "#5b6b7d", fontWeight: 600 }}>
                    Your name <span style={{ fontWeight: 400 }}>(optional)</span>
                  </label>
                  <input
                    style={{ ...field, marginTop: 5, marginBottom: 12 }}
                    type="text"
                    maxLength={80}
                    placeholder="So we can help you if you call us"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleVerify()}
                  />

                  <button style={primary(busy)} disabled={busy} onClick={handleVerify}>
                    {busy ? "Verifying…" : "Verify and continue"}
                  </button>

                  <div style={{ marginTop: 10, textAlign: "center", fontSize: 12.5 }}>
                    {cooldown > 0 ? (
                      <span style={{ color: "#8a97a6" }}>Resend OTP in {cooldown}s</span>
                    ) : (
                      <button
                        onClick={handleSend}
                        disabled={busy}
                        style={{
                          background: "none",
                          border: "none",
                          color: SIR_THEME.accent,
                          fontWeight: 600,
                          cursor: "pointer",
                          padding: 0,
                        }}
                      >
                        Resend OTP
                      </button>
                    )}
                    <span style={{ color: "#cfd8e3", margin: "0 8px" }}>|</span>
                    <button
                      onClick={() => {
                        setSent(false);
                        setOtp("");
                        setNote(null);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: SIR_THEME.accent,
                        fontWeight: 600,
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      Change number
                    </button>
                  </div>
                </>
              ) : (
                <button style={primary(busy)} disabled={busy} onClick={handleSend}>
                  {busy ? "Sending…" : "Send OTP on WhatsApp"}
                </button>
              )}

              <p style={{ fontSize: 11.5, color: "#8a97a6", marginTop: 12, marginBottom: 0, lineHeight: 1.5 }}>
                We use your number only to verify you and to keep count of your
                searches. You get 5 more searches after verification.
              </p>
            </>
          ) : (
            <>
              <p style={{ fontSize: 13.5, color: "#33475b", marginTop: 0, lineHeight: 1.55 }}>
                1 credit = 1 voter's full details. Credits never expire, and a
                voter you have already opened is never charged twice.
              </p>

              <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
                {plans.map((p) => (
                  <div
                    key={p.PlanId}
                    style={{
                      border: "1px solid #dbe3ec",
                      borderRadius: 10,
                      padding: "10px 12px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{p.Label}</div>
                      <div style={{ fontSize: 11.5, color: "#8a97a6" }}>
                        {p.Credits} credits
                      </div>
                    </div>
                    <div style={{ fontWeight: 800, fontSize: 16, color: SIR_THEME.accent }}>
                      ₹{p.Amount}
                    </div>
                  </div>
                ))}
                {plans.length === 0 ? (
                  <div style={{ fontSize: 13, color: "#8a97a6" }}>Loading plans…</div>
                ) : null}
              </div>

              {/* Online payment is phase 3. Until the gateway is wired, the
                  honest thing is to say so and give a working way to pay,
                  rather than show a Buy button that does nothing. */}
              <a
                href={
                  "https://wa.me/" +
                  (support || "919767728530") +
                  "?text=" +
                  encodeURIComponent(
                    "Hello, I want to buy SmartSearch credits for mobile number " +
                    (mobile || "")
                  )
                }
                target="_blank"
                rel="noreferrer"
                style={{
                  ...primary(false),
                  display: "block",
                  textAlign: "center",
                  textDecoration: "none",
                  background: "#25D366",
                }}
              >
                Buy credits on WhatsApp
              </a>

              <p style={{ fontSize: 11.5, color: "#8a97a6", marginTop: 12, marginBottom: 0, lineHeight: 1.5 }}>
                Online card / UPI payment is coming shortly. Message us and your
                credits are added to your verified number straight away.
              </p>

              <div style={{ marginTop: 12, textAlign: "center" }}>
                <button
                  onClick={() => {
                    clearSirSession();
                    setStep("otp");
                    setSent(false);
                    setOtp("");
                    setMobile("");
                    setNote(null);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: SIR_THEME.accent,
                    fontSize: 12.5,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Use a different mobile number
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SirAccessModal;
